import classNames from 'classnames';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { cloneDeep, get, isEqual, set, sumBy } from 'lodash';
import {
  DealerCashRuleType,
  FinanceType,
  Origin,
} from '@upstart/harmony/lib/constants';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { getResidualValueFromPercent } from '@upstart/harmony/lib/payment-utils';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { showBiweeklyPayments } from 'util/deskingUtils';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { isDefined } from '@upstart/harmony/lib/legos/utils/tsutils';
import OfferCardHeader from '../OfferCardHeader';
import {
  getDealerCashRuleAppliedValue,
  getPeriodicPaymentFromOffer,
} from '../../../util/paymentUtils';
import { parseFloatValue } from '../../PencilEditor/Input';
import { DO_NOT_LOG_PROPERTIES, RATE_TO_APR } from '../../../util/constants';
import {
  calculateAndSaveMoneyFactor,
  calculateAndSaveRatePercentage,
  getDefaultResidualPercentage,
  getMrm,
  getOemOfferForOffer,
  getResidualPercentage,
  isOnePayLease,
  logTradeInProperty,
  updateAndLogProperty,
  updateDealerCashRule,
  updateMonthsMiles,
} from './desking-offer-utils';
import DeskingOfferDataTable from './DeskingOfferDataTable';
import { DeskingOfferCardProps, NormalizedOffer } from './types';
import './DeskingOfferCard.scss';
import { isDMSFlowEnabled } from '../../../util/dmsUtils';

const MAX_VALUE = 999999;
const DMS_WITH_DESKING_OFFER_PUSH: string[] = [
  DMSType.CDK,
  DMSType.DT,
  DMSType.TEKION,
];

const OEM_PROP_MAP: Record<string, 'apr' | 'cash'> = {
  apr: 'apr',
  rate: 'apr',
  rebate: 'cash',
  // residuals do not come from oem offers
  // term and mileage are handled in onTermOrMileageChange
};

const isValid = (value: number, max: number) => value <= max;

const shouldEnablePushToDMS = (dealer: Dealer) =>
  dealer.dmsType &&
  DMS_WITH_DESKING_OFFER_PUSH.includes(dealer.dmsType) &&
  isDMSFlowEnabled(dealer);

const populateActions = (props: DeskingOfferCardProps) => {
  const {
    dealer,
    onPushToCRM,
    onPushToDMS,
    onPushToCRMDisabled,
    onDuplicate,
    onRemove,
    onOpenPencilHistory,
  } = props;
  const actions = [];
  if (onPushToCRM) {
    actions.push({
      label: 'Push to CRM',
      // We should display alert message only when there is an accepted offer and
      // the selected offer is not an accepted offer.
      onClick: () =>
        onPushToCRM(props.offer, !!props.hasAcceptedOffer && !props.accepted),
      disabled: !!onPushToCRMDisabled,
    });
  }
  if (shouldEnablePushToDMS(dealer) && onPushToDMS) {
    actions.push({
      label: 'Push to DMS',
      onClick: () => onPushToDMS(props.offer),
    });
  }
  if (onOpenPencilHistory) {
    actions.push({
      label: 'Pencil Version History',
      onClick: () => {
        onOpenPencilHistory(props.offer.id);
      },
    });
  }
  if (onDuplicate) {
    // Remove ID from payload
    const { id, ...input } = props.offer;
    actions.push({
      label: 'Duplicate',
      onClick: () => onDuplicate(input),
    });
  }
  if (onRemove) {
    actions.push({
      label: 'Remove',
      onClick: () => onRemove(props.offer),
    });
  }
  return actions;
};

const getMonthlyPayment = ({
  dealer,
  offer,
  offerPayment,
}: Pick<DeskingOfferCardProps, 'dealer' | 'offer' | 'offerPayment'>) => {
  const isPriceRangeEnabled = get(
    offer,
    'data.show10DollarMonthlyPriceRange',
    get(dealer, 'desking.show10DollarMonthlyPriceRangeByDefault'),
  );

  const estimatedPeriodicPayment = getPeriodicPaymentFromOffer(
    offerPayment ?? {},
    showBiweeklyPayments(dealer),
  );

  if (offer.data?.financeType === FinanceType.LEASE) {
    if (estimatedPeriodicPayment) {
      const returnValue = [estimatedPeriodicPayment];
      if (isPriceRangeEnabled) {
        returnValue.push(
          estimatedPeriodicPayment +
            get(dealer, 'desking.monthlyPriceRangeValue'),
        );
      }
      return returnValue;
    }
  } else if (offer.data?.financeType === FinanceType.PURCHASE) {
    if (estimatedPeriodicPayment) {
      const returnValue = [estimatedPeriodicPayment];
      if (isPriceRangeEnabled) {
        returnValue.push(
          estimatedPeriodicPayment +
            get(dealer, 'desking.monthlyPriceRangeValue'),
        );
      }
      return returnValue;
    }
  }
  return undefined;
};

const calculatePropValue = (props: DeskingOfferCardProps, field: string) => {
  switch (field) {
    case 'mrm':
      return getMrm(props);
    case 'ratePercentage':
      return (props.offer.data as any).rate * RATE_TO_APR;
    case 'onePayLeaseApr':
      return (props.offer.data as any).onePaymentRate * RATE_TO_APR;
    case 'residualPercentage':
      return getResidualPercentage(props);
    case 'onePaymentRate':
      return props.offerPayment?.onePaymentRate;
  }
  return null;
};

// This function will add any necessary properties to the offer that may not be there when it's first passed in.
function getNormalizedOffer(props: DeskingOfferCardProps) {
  const offer: NormalizedOffer = cloneDeep(props.offer);

  if (offer.data?.financeType === FinanceType.LEASE) {
    offer.data.mrm = offer.data.mrm ?? calculatePropValue(props, 'mrm');
    offer.data.ratePercentage = calculatePropValue(props, 'ratePercentage');
    offer.data.residualPercentage =
      offer.data.residualPercentage ??
      calculatePropValue(props, 'residualPercentage');

    if (isOnePayLease(props)) {
      offer.data.onePaymentRate =
        offer.data.onePaymentRate ??
        calculatePropValue(props, 'onePaymentRate');
      offer.data.onePayLeaseApr =
        offer.data.onePayLeaseApr ??
        calculatePropValue(props, 'onePayLeaseApr');
    }
  }

  return offer;
}

const DeskingOfferCard: FunctionComponent<DeskingOfferCardProps> = props => {
  const [localOfferState, setLocalOfferState] = useState<NormalizedOffer>(
    getNormalizedOffer(props),
  );

  const defaultResidualPercentage = useMemo(
    () => getDefaultResidualPercentage(props),
    [props],
  );

  useEffect(() => {
    // Update offer when changes come from above
    setLocalOfferState(getNormalizedOffer(props));
    // only trigger the update when a certain set of properties change
    // eslint-disable-next-line
  }, [props.offer, props.car, props.deal, props.dealer]);

  const onSelectedChange = (input: boolean) => {
    const offer = cloneDeep(localOfferState);
    set(offer, 'isSelectedOffer', input);
    setLocalOfferState(offer);
    props.updateDeskingOffers([offer]);
  };

  // Separate function to change months and miles simultaneously
  const onMonthsMilesChange = (input: {
    months?: number;
    mileage?: number;
    isMSIncentivesEnabled?: boolean;
  }) => {
    const offer = cloneDeep(localOfferState);
    updateMonthsMiles({
      offer,
      months: input.months,
      mileage: input.mileage,
      car: props.car,
      dealer: props.dealer,
      dealerPriceRules: props.dealerPriceRules,
      currentUser: props.currentUser,
    });
    set(offer, `sentToSalespersonAt`, null);
    setLocalOfferState(offer);
    props.onChange(offer, input.isMSIncentivesEnabled);
  };

  /**
   * Return car's descriptive name consisting of the year, model and make.
   * @param offer  desking offer
   * @param propertyPath  path of the trade-in property being modified, e.g. pencilTradeIns.0.payOff
   * @returns
   */
  const getTradeInCarName = (offer: DeskingOffer, propertyPath: string) => {
    const pathnames = propertyPath.split('.');
    const index = pathnames.length > 1 ? +pathnames[1] : undefined;
    if (isDefined(index) && !isNaN(index)) {
      const tradeIn = offer.data?.pencilTradeIns?.[index];
      const year = tradeIn?.year?.name ?? '';
      const make = tradeIn?.make?.name ?? '';
      const model = tradeIn?.model?.name ?? '';
      return `${year} ${make} ${model}`.trim();
    }
    return '';
  };

  /**
   * Handles a trade-ins property change (any one of the data.pencilTradeIns[n].* properties)
   * @param newOffer         the deskingOffer object
   * @param propertyPath     the property path, e.g. pencilTradeIns[0].payOff
   * @param primaryField     the property name, e.g. payOff
   * @param aggregateField   the property that stores the total, e.g. tradePayoff
   * @param parsedValue      new value
   * @param parsedOldValue   old value
   * @returns callback function to update the other offers
   */
  const handleTradeInFieldChange = (
    newOffer: NormalizedOffer,
    propertyPath: string,
    primaryField: string,
    aggregateField: string,
    parsedValue?: number | boolean | null,
    parsedOldValue?: number | boolean | null,
  ): (() => Promise<void>) => async () => {
    // get trade in list
    const tradeIns = get(newOffer, ['data', 'pencilTradeIns']);

    // calculate total value
    const aggregateValue = sumBy(tradeIns, primaryField);

    // get ids of all desking offers
    const offerIds = props.offers?.map(offer => offer.id) ?? [];

    // list of field:value pairs to update in each desking offer
    const fieldValuePairs: [string, any][] = [
      [aggregateField, aggregateValue],
      [propertyPath, parsedValue],
    ];

    if (primaryField === 'payOff') {
      // set `payoffEstimated` to true, as we are manually changing the payoff field to an estimated value.
      // this will only be set to false by a backend change on payoff lookup
      const payOffEstimatedField = `${propertyPath.substring(
        0,
        propertyPath.lastIndexOf('.'),
      )}.payoffEstimated`;
      fieldValuePairs.push([payOffEstimatedField, true]);
    }

    const offersWithUpdatedTradeIns = props.applyChangesToOffers(
      offerIds,
      fieldValuePairs,
    );
    // update changelog
    const offerToUpdate = offersWithUpdatedTradeIns.find(
      o => o.id === newOffer.id,
    );
    if (offerToUpdate) {
      const carName = getTradeInCarName(offerToUpdate, propertyPath);
      const propName = `${carName} ${primaryField}`.trim();
      logTradeInProperty({
        offer: offerToUpdate,
        propName,
        newValue: parsedValue,
        oldValue: parsedOldValue,
        shouldLog: true,
        currentUser: props.currentUser,
      });
    }
    // update desking offers
    await props.saveOffers(offersWithUpdatedTradeIns, true);
  };

  const onFieldChange = (
    field: string,
    type?: 'price' | 'percentage' | 'number' | 'checkbox',
    maxValue?: number | null,
  ) => (value: any) => {
    const newOffer = cloneDeep(localOfferState);
    const parsedValue =
      // eslint-disable-next-line no-nested-ternary
      type === 'price'
        ? parseCurrencyTruncated(value)
        : type === 'percentage'
        ? parseFloatValue(value)
        : value;
    const oldValue =
      get(props.offer, `data.${field}`) ?? calculatePropValue(props, field);
    const parsedOldValue =
      // eslint-disable-next-line no-nested-ternary
      type === 'price'
        ? parseCurrencyTruncated(oldValue)
        : type === 'percentage'
        ? parseFloatValue(oldValue)
        : oldValue;

    const max = maxValue || MAX_VALUE;

    // Compare the new value with the persisted value to determine if a change has occurred.
    const hasChanged = !isEqual(parsedValue, parsedOldValue);

    // Does value meet max value validation rules?
    if (!hasChanged || (type !== 'checkbox' && !isValid(parsedValue, max))) {
      return;
    }

    const oemProperty = OEM_PROP_MAP[field];
    // if there's an property, that's derived from an incentive program, being changed
    // verify whether the incentive programs for this offer need to be cleared
    if (oemProperty) {
      const oemOffer = getOemOfferForOffer(props);

      let hasOemPropChanged = oemProperty in oemOffer;
      const comparisonValue =
        field === 'rate' ? parsedValue * RATE_TO_APR : parsedValue;
      hasOemPropChanged = oemOffer[oemProperty] !== comparisonValue;

      // if no matching oem offer was found, or the property changed from what was in the oem offer
      // clear the incentive programs in this offer
      if (!oemOffer || hasOemPropChanged) {
        set(newOffer, 'data.selectedCustomerTypeId', undefined);
        updateAndLogProperty({
          offer: newOffer,
          propName: 'programs',
          newValue: [],
          currentUser: props.currentUser,
        });
      }
    }

    // DownPayment may not be greater than invoice
    if (field === 'invoice') {
      const downPayment = get(props.offer, ['data', `downPayment`], 0);
      if (downPayment > parsedValue) {
        updateAndLogProperty({
          offer: newOffer,
          propName: 'downPayment',
          newValue: parsedValue,
          oldValue: parsedOldValue,
          currentUser: props.currentUser,
        });
      }
    }

    // Update pencil properties
    const primaryField = field.split('.').slice(-1)[0];
    updateAndLogProperty({
      offer: newOffer,
      propName: primaryField,
      newValue: parsedValue,
      oldValue: parsedOldValue,
      shouldLog: !DO_NOT_LOG_PROPERTIES.includes(primaryField),
      currentUser: props.currentUser,
    });
    set(newOffer, `sentToSalespersonAt`, null);
    set(newOffer, `data.${field}`, parsedValue);

    // Perform any side effects from one value changing onto other values
    let applyTradeInChange: null | (() => void) = null;
    switch (primaryField) {
      case 'dealerCash': {
        const dealerCashRule = getDealerCashRuleAppliedValue(props.offer.data);
        const normalizedValue = Math.min(dealerCashRule, parsedValue);
        const useFullDealerCash = normalizedValue === parsedValue;
        const newChangeLog = changeLogHelper.updateDealerCashRule(
          newOffer,
          useFullDealerCash ? 100 : normalizedValue,
          parsedOldValue,
        );
        set(newOffer, 'changeLog', newChangeLog);
        set(
          newOffer,
          ['data', 'dealerCashRule'],
          useFullDealerCash
            ? {
                type: DealerCashRuleType.PRICE_OFFSET_PERCENT,
                value: 100,
              }
            : {
                type: DealerCashRuleType.PRICE_OFFSET_FIXED_MAX,
                value: normalizedValue,
              },
        );
        // Reset programs
        updateAndLogProperty({
          offer: newOffer,
          propName: 'programs',
          newValue: [],
          currentUser: props.currentUser,
        });
        // Reset selected customer type
        set(newOffer, 'data.selectedCustomerTypeId', undefined);
        break;
      }
      case 'dealerCashRule': {
        updateDealerCashRule({
          oldOffer: props.offer,
          newDealerCashRuleValue: parsedValue,
          offerToUpdate: newOffer,
          currentUser: props.currentUser,
        });
        break;
      }
      case 'mrm': {
        if (props.offer.data?.financeType === FinanceType.LEASE) {
          const residualPercentage = newOffer.data.residualPercentage ?? 0;
          const residual = getResidualValueFromPercent(
            props.dealer,
            props.car,
            parsedValue,
            residualPercentage * 100,
            props.offer.data.msrp ?? 0,
          );
          updateAndLogProperty({
            offer: newOffer,
            propName: 'residual',
            newValue: residual,
            currentUser: props.currentUser,
          });
        }
        break;
      }
      case 'msrp': {
        if (props.offer.data?.financeType === FinanceType.LEASE) {
          const residualPercentage = newOffer.data.residualPercentage ?? 0;
          const residual = getResidualValueFromPercent(
            props.dealer,
            props.car,
            newOffer.data?.mrm,
            residualPercentage * 100,
            parsedValue,
          );
          updateAndLogProperty({
            offer: newOffer,
            propName: 'residual',
            newValue: residual,
            currentUser: props.currentUser,
          });
        }
        break;
      }
      case 'ratePercentage': {
        // convert percentage to money factor
        calculateAndSaveMoneyFactor({
          offer: newOffer,
          propName: 'rate',
          newRatePercentage: parsedValue,
          currentUser: props.currentUser,
        });
        break;
      }
      case 'onePayLeaseApr': {
        // convert percentage to money factor
        calculateAndSaveMoneyFactor({
          offer: newOffer,
          propName: 'onePaymentRate',
          newRatePercentage: parsedValue,
          currentUser: props.currentUser,
        });
        break;
      }
      case 'rate': {
        // convert rate percentage to rate
        calculateAndSaveRatePercentage({
          offer: newOffer,
          newRate: parsedValue,
          currentUser: props.currentUser,
        });
        break;
      }
      case 'residual': {
        if (props.offer.data?.financeType === FinanceType.LEASE) {
          // calculate percentage from the amount
          const residualPercentage = getResidualPercentage({
            car: props.car,
            dealer: props.dealer,
            offer: newOffer,
          });
          updateAndLogProperty({
            offer: newOffer,
            propName: 'residualPercentage',
            newValue: residualPercentage,
            currentUser: props.currentUser,
          });
        }
        break;
      }
      case 'residualPercentage': {
        if (props.offer.data?.financeType === FinanceType.LEASE) {
          // convert percentage back to amount
          const residual = getResidualValueFromPercent(
            props.dealer,
            props.car,
            newOffer.data.mrm,
            parsedValue * 100,
            props.offer.data.msrp ?? 0,
          );
          updateAndLogProperty({
            offer: newOffer,
            propName: 'residual',
            newValue: residual,
            currentUser: props.currentUser,
          });
        }
        break;
      }
      case 'tradeInAllowance': {
        applyTradeInChange = handleTradeInFieldChange(
          newOffer,
          field,
          'tradeInAllowance',
          'tradeInAllowance',
          parsedValue,
          parsedOldValue,
        );
        break;
      }
      case 'tradeInTaxCredit': {
        applyTradeInChange = handleTradeInFieldChange(
          newOffer,
          field,
          'tradeInTaxCredit',
          'tradeInTaxCredit',
          parsedValue,
          parsedOldValue,
        );
        break;
      }
      case 'tradeACV': {
        applyTradeInChange = handleTradeInFieldChange(
          newOffer,
          field,
          'tradeACV',
          'tradeACV',
          parsedValue,
          parsedOldValue,
        );
        break;
      }
      case 'payOff': {
        applyTradeInChange = handleTradeInFieldChange(
          newOffer,
          field,
          'payOff',
          'tradePayoff',
          parsedValue,
          parsedOldValue,
        );
        break;
      }
      case 'addInceptionFeesToTotalAtStart':
      case 'show10DollarMonthlyPriceRange': {
        const otherOfferIds = (props.offers ?? [])
          .filter(
            o =>
              o?.data?.financeType === FinanceType.PURCHASE ||
              (o?.data?.financeType === FinanceType.LEASE &&
                o.id !== newOffer.id),
          )
          .map(o => o.id);
        const offersWithUpdatedTradeIns = props.applyChangesToOffers(
          otherOfferIds,
          [[field, parsedValue]],
        );
        props.saveOffers(offersWithUpdatedTradeIns, true);
        break;
      }
    }

    setLocalOfferState(newOffer);

    // handle trade updates separately
    if (applyTradeInChange) {
      applyTradeInChange();
    } else {
      props.onChange(newOffer);
    }
  };

  const actions = populateActions(props);
  const messagingOrigin =
    props.deal.origin === Origin.INSTORE || props.deal.lastOpenedInStore
      ? Origin.INSTORE
      : props.deal.origin;

  return (
    <div
      className={classNames('offer-container', {
        accepted: props.accepted,
        'all-offers': props.displayingAllSelectedOffers,
        readOnlyMode: props.readOnlyMode && props.isSelectedForPencilHistory,
      })}
    >
      <OfferCardHeader
        offer={props.offer}
        errors={props.errors}
        localOfferState={localOfferState}
        onSelectedChange={onSelectedChange}
        onChange={onMonthsMilesChange}
        financeType={props.offer.data?.financeType}
        messagingOrigin={messagingOrigin}
        months={localOfferState.data?.months}
        mileage={localOfferState.data?.mileage}
        monthlyPayment={getMonthlyPayment(props)}
        showBiweeklyPayments={showBiweeklyPayments(props.dealer)}
        onePayment={
          props.deal.onePayLease ? props.offerPayment?.onePayment : null
        }
        cashPayment={props.offerPayment?.balance}
        actions={actions}
        displayingAllSelectedOffers={props.displayingAllSelectedOffers}
        onPaymentRollbackChange={onFieldChange}
        readOnlyMode={props.readOnlyMode}
        isReadOnlyLoading={props.isReadOnlyLoading}
        isViewingCurrentPencilVersion={props.isViewingCurrentPencilVersion}
        dealer={props.dealer}
      />
      <DeskingOfferDataTable
        {...props}
        localOfferState={localOfferState}
        setLocalOfferState={setLocalOfferState}
        defaultResidualPercentage={defaultResidualPercentage}
        onFieldChange={onFieldChange}
        offerPayment={props.offerPayment}
        errors={props.errors}
      />
      {!props.readOnlyMode && props.accepted && (
        <div className="selected-offer-frame">
          <div className="selected-offer-title">Offer Accepted By Customer</div>
        </div>
      )}
      {props.readOnlyMode && props.isSelectedForPencilHistory && (
        <div className="selected-for-pencil-history" />
      )}
    </div>
  );
};

export default DeskingOfferCard;

export const test = {
  getNormalizedOffer,
};
